import { Store } from "lucide-react";
import type { Marketplace } from "../../domain/types";
import { cn } from "../../lib/cn";

const marketplaceWordmarkAssets: Partial<Record<Marketplace, string>> = {
  ebay: "/marketplaces/ebay.svg",
  etsy: "/marketplaces/etsy.svg",
  shopify: "/marketplaces/shopify.svg",
  vinted: "/marketplaces/vinted.svg"
};

const marketplaceNames: Record<Marketplace, string> = {
  depop: "Depop",
  ebay: "eBay",
  etsy: "Etsy",
  other: "Other marketplace",
  shopify: "Shopify",
  vinted: "Vinted"
};

const marketplaceWordmarks = new Set<Marketplace>(["depop", "ebay", "etsy", "shopify", "vinted"]);

export function marketplaceMarkIncludesLabel(marketplace: Marketplace) {
  return marketplaceWordmarks.has(marketplace);
}

const depopLogoPath =
  "m271.72 446.84c0-57.81 41.3-93.48 107.37-93.48h67.86v-85h86.07v266.7h-154.3c-65.7 0-107-34.16-107-88.22Zm89.94-2.97c0 22.17 13.53 33.81 39.45 33.81h45.84v-68h-45.46c-25.93 0-39.83 11.65-39.83 34.19Zm725.32-.71c0 58.15-41.54 94.02-108 94.02h-68.76v85h-86.07V354.42h155.2c66.08 0 107.62 34.36 107.62 88.73Zm-91.47 3.52c0-22.17-13.53-33.81-39.45-33.81h-45.84v68h45.46c25.93 0 39.83-11.65 39.83-34.19Zm652.49-3.52c0 58.15-41.54 94.02-108 94.02h-68.76v85h-86.07V354.42h155.2c66.08 0 107.62 34.36 107.62 88.73Zm-91.47 3.52c0-22.17-13.53-33.81-39.45-33.81h-45.84v68h45.46c25.93 0 39.83-11.65 39.83-34.19Zm-445.6-2.55c0-64.62 51.85-99.18 123.24-99.18s123.24 34.56 123.24 99.18-51.85 99.94-123.24 99.94-123.24-35.32-123.24-99.94Zm163.07 0c0-31.56-15.78-47.34-39.83-47.34s-39.83 15.78-39.83 47.34 15.78 48.09 39.83 48.09 39.83-16.91 39.83-48.09Zm-477.47 4.84c0-64.62-41.71-103.69-117.23-103.69-72.51 0-119.11 39.07-119.11 100.31s46.22 98.81 123.24 98.81c48.47 0 87.54-18.03 108.21-47.34l-61.36-22.35c-10.68 11.53-26.64 18-43.54 18-25.44 0-43.03-10.24-47.65-27.5-.3-1.12-.55-2.27-.74-3.46h158.18v-12.77Zm-158.18-27.6c1.97-21.3 16.57-34.32 41.82-34.32s39.85 13.81 39.85 34.32Z";

export function MarketplaceMark({
  marketplace,
  className,
  labelled = false
}: {
  marketplace: Marketplace;
  className?: string;
  labelled?: boolean;
}) {
  const wordmarkAsset = marketplaceWordmarkAssets[marketplace];
  const title = marketplaceNames[marketplace];
  const accessibilityProps = labelled
    ? { role: "img" as const, "aria-label": title }
    : { "aria-hidden": true };

  if (marketplace === "depop") {
    return (
      <span className={cn("marketplace-mark marketplace-mark-depop", className)} {...accessibilityProps}>
        <svg viewBox="250 250 1420 390" focusable="false">
          <path d={depopLogoPath} />
        </svg>
      </span>
    );
  }

  if (!wordmarkAsset) {
    return (
      <span className={cn("marketplace-mark marketplace-mark-other", className)} {...accessibilityProps}>
        <Store />
      </span>
    );
  }

  return (
    <span
      className={cn(`marketplace-mark marketplace-mark-${marketplace}`, className)}
      {...accessibilityProps}
    >
      <img src={wordmarkAsset} alt="" draggable={false} />
    </span>
  );
}
